import { GlobalMessages } from '@shared/localization/messages';
import { useEffect, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';

import IconSearch from '@/assets/Icons/IconSearch';
import { useMessages } from '@/hooks/messages';

interface SearchProps {
  handleOnChange: (searchedString: string) => void;
}

const Search = ({ handleOnChange }: SearchProps) => {
  const globalMessages = useMessages(GlobalMessages);

  const [inputValue, setInputValue] = useState('');

  const [debouncedValue] = useDebounce(inputValue, 500);

  // EWM-1140: the callback is held in a ref so this effect depends on the debounced VALUE only.
  //
  // It used to list `handleOnChange` in its dependencies. A parent passing an inline arrow hands
  // down a new identity on every render, so the effect re-ran every render, called the callback,
  // and - if the callback set state - the parent re-rendered and produced another new arrow. That
  // is an unbounded cycle, and the Management Report hit it: it passed
  // `(text) => filterDispatch({ type: 'SET_SEARCH_TEXT', payload: text })` while the reducer
  // returned a fresh state object even for unchanged text, so React never bailed out. Measured on
  // TEST 2026-09-23, that tab committed ~77 renders per second indefinitely with nobody touching
  // it, while every other tab sat at 0.
  //
  // Fixing it here rather than at the call site is deliberate: seven of the eight call sites pass a
  // stable setter and were fine by luck, not by design. A ref removes the footgun for all of them.
  const handleOnChangeRef = useRef(handleOnChange);

  // Synced in an effect, not during render: writing a ref while rendering is unsafe under
  // concurrent React, and `react-hooks/refs` rejects it. Declared before the effect below so that
  // a commit which changes both the callback and the value uses the newer callback.
  useEffect(() => {
    handleOnChangeRef.current = handleOnChange;
  }, [handleOnChange]);

  useEffect(() => {
    handleOnChangeRef.current(debouncedValue);
  }, [debouncedValue]);

  return (
    <div className='relative'>
      <input
        type='text'
        placeholder={globalMessages.search()}
        className='rounded-6 form-input h-12 placeholder:tracking-wider focus:!placeholder-transparent dark:!bg-transparent placeholder:dark:text-white'
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
      />

      <button
        type='button'
        className='rounded-6 absolute inset-y-0 right-1 m-auto flex h-9 w-9 items-center justify-center p-0 dark:!bg-transparent'
      >
        <IconSearch className='dark:text-white' />
      </button>
    </div>
  );
};

export default Search;
